import React, { forwardRef } from 'react';
import Button from './button';

const EmptyState = forwardRef(({
    variant = 'no-data', // 'not-measured' | 'no-data' | 'source-error'
    title,
    description,
    reason,
    icon,
    action,
    onRetry,
    compact = false,
    className = '',
    style,
    ...props
}, ref) => {
    const isNotMeasured = variant === 'not-measured' || variant === 'not_measured';
    const isSourceError = variant === 'source-error' || variant === 'source_error' || variant === 'error';
    const isNoData = !isNotMeasured && !isSourceError;

    const defaultTitle = isNotMeasured
        ? '— Not measured'
        : isSourceError
        ? 'Source Error'
        : 'No data available';

    const finalTitle = title || defaultTitle;
    const finalDescription = description || reason;

    // Icons
    const renderIcon = () => {
        if (icon) return icon;
        if (isNotMeasured) {
            return (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="8" y1="12" x2="16" y2="12" />
                </svg>
            );
        }
        if (isSourceError) {
            return (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
            );
        }
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <path d="M10 4v4" />
                <path d="M2 8h20" />
                <line x1="6" y1="12" x2="18" y2="12" />
            </svg>
        );
    };

    const containerStyle = {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: compact ? '1.5rem' : '2.5rem 1.5rem',
        borderRadius: 'var(--radius-lg)',
        boxSizing: 'border-box',
        fontFamily: 'var(--font-ui)',
        ...(isNotMeasured ? {
            backgroundColor: 'var(--surface-sunken)',
            border: '1px dashed var(--border)',
            color: 'var(--text-muted)'
        } : isSourceError ? {
            backgroundColor: 'var(--danger-soft)',
            border: '1px solid var(--danger-border)',
            color: 'var(--text)'
        } : {
            backgroundColor: 'var(--surface-raised)',
            border: '1px dashed var(--border)',
            color: 'var(--text)'
        }),
        ...style
    };

    const iconContainerStyle = {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '3rem',
        height: '3rem',
        borderRadius: 'var(--radius-pill)',
        marginBottom: '0.75rem',
        ...(isNotMeasured ? {
            backgroundColor: 'var(--surface)',
            color: 'var(--text-muted)'
        } : isSourceError ? {
            backgroundColor: 'var(--danger-soft)',
            color: 'var(--danger)'
        } : {
            backgroundColor: 'var(--accent-soft)',
            color: 'var(--accent-ink)'
        })
    };

    return (
        <div ref={ref} className={className} style={containerStyle} {...props}>
            <div style={iconContainerStyle}>
                {renderIcon()}
            </div>
            {isNotMeasured && (
                <span style={{ fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
                    Not measured / Не измерено
                </span>
            )}
            <h4
                style={{
                    margin: 0,
                    fontSize: compact ? '0.9375rem' : '1.0625rem',
                    fontWeight: '600',
                    color: isSourceError ? 'var(--danger)' : 'var(--text)',
                    fontFamily: isNoData ? 'var(--font-display)' : 'var(--font-ui)'
                }}
            >
                {finalTitle}
            </h4>
            {finalDescription && (
                <p
                    style={{
                        margin: '0.375rem 0 0',
                        fontSize: '0.8125rem',
                        lineHeight: '1.4',
                        color: 'var(--text-muted)',
                        maxWidth: '28rem'
                    }}
                >
                    {finalDescription}
                </p>
            )}
            {(action || onRetry) && (
                <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
                    {action}
                    {onRetry && (
                        <Button
                            variant={isSourceError ? 'danger' : 'secondary'}
                            size="sm"
                            onClick={onRetry}
                        >
                            Retry / Повторить
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
});

EmptyState.displayName = 'EmptyState';

export { EmptyState };
export default EmptyState;
